<template>
  <div class="ae">

    <div ref="grid" class="ae-grid" :class="{ 'is-resizing': resizing }" :style="convWidth ? { '--ae-conv': `${convWidth}px` } : null">
      <AgentConversation
        ref="conversation"
        :entry="entry"
        :context="context"
        :session-id="sessionId"
        :storage-key="storageKey"
        :auto-start="autoStart"
        @session="onSession"
        @reset="onReset"
        @goto-artifact="scrollToArtifact"
      />

      <div
        class="ae-splitter"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize conversation"
        title="Drag to resize; double-click to reset"
        @pointerdown="startResize"
        @dblclick="resetSplit"
      />

      <section class="ae-card ae-workspace" aria-label="Workspace">
        <div class="ae-work-head">
          <div class="ae-card-head">
            <div class="ae-title">Workspace</div>
            <div class="ae-head-right">
              <a v-if="canUpload" href="#" class="ae-link ae-btn-tiny ae-btn-icon" title="Upload a file for the agent to read" aria-label="Upload" @click.prevent="showUpload = true"><font-awesome-icon :icon="uploadIcon" /></a>
              <span class="ae-state">
                <span class="ae-dot" :style="{ background: sessionPill.dot }" />
                <span>{{ sessionPill.text }}</span>
              </span>
            </div>
          </div>
          <div v-if="lanes.length > 1" class="ae-lanes" role="tablist" aria-label="Filter workspace">
            <button
              v-for="l in lanes"
              :key="l.id"
              type="button"
              role="tab"
              class="ae-lane"
              :class="{ 'is-on': lane === l.id }"
              :aria-selected="lane === l.id ? 'true' : 'false'"
              @click="lane = l.id"
            >
              {{ l.label }}<span class="ae-lane-n">{{ l.count }}</span>
            </button>
          </div>
        </div>

        <div class="ae-work-body">
        <div ref="artifactScroll" class="ae-stack">
          <div v-if="!items.length && !s.files.length" class="ae-empty">
            <div class="ae-empty-body">Everything the agent makes lands here in the order it was made: queries, drafts, charts, pages, files, runs. The newest opens automatically; click a header to open an earlier one.</div>
          </div>

          <div
            v-for="a in visibleItems"
            :id="artifactDomId(a.key)"
            :key="a.key"
            class="ae-fold"
            :class="{ 'is-open': isOpen(a), 'is-unsaved': isUnsaved(a) }"
          >
            <div class="ae-fold-row">
            <button type="button" class="ae-fold-head" :title="rowHint(a)" @click="toggleFold(a)">
              <span class="ae-fold-caret">{{ isOpen(a) ? '▾' : '▸' }}</span>
              <font-awesome-icon :icon="kindIcon(rowIcon(a))" class="ae-kind-icon" :style="{ color: kindColor(rowIcon(a)) }" />
              <span class="ae-fold-kind">{{ a.label }}</span>
              <span class="ae-fold-name" :class="{ 'ae-mono': isMono(a) }">{{ rowName(a) }}</span>
              <span v-if="a.kind === 'draft'" class="ae-draft-chip" :class="`is-${a.data.status}`">{{ draftChip(a) }}</span>
              <span v-else-if="a.kind === 'frame' && frameRows(a) != null" class="ae-draft-chip">{{ formatCount(frameRows(a)) }} rows</span>
              <span v-else-if="a.kind === 'report'" class="ae-draft-chip">{{ formatBytes(a.data.size) }} · Download</span>
              <span v-else-if="a.kind === 'workbook'" class="ae-draft-chip">{{ (a.data.sheets || []).length }} sheet{{ (a.data.sheets || []).length === 1 ? '' : 's' }}</span>
              <span v-else-if="a.runs.length > 1" class="ae-draft-chip">{{ a.runs.length }} runs</span>
              <span v-if="isUnsaved(a)" class="ae-unsaved-pill">Unsaved</span>
              <span class="ae-dot" :style="{ background: rowDot(a) }" />
            </button>
            <button v-if="a.kind !== 'draft'" type="button" class="ae-x" :title="a.kind === 'saved_page' ? 'Delete this page from the platform' : 'Remove from the workspace'" @click.stop="a.kind === 'saved_page' ? deleteSavedPage(entry, a) : removeItem(entry, a)">×</button>
            <span v-if="isOpen(a) && sessionActive" class="ae-fold-actions">
              <template v-if="a.kind === 'cell'">
                <template v-if="a.editing">
                  <a href="#" class="ae-link" @click.prevent="saveEdit(a)">Save</a>
                  <a href="#" class="ae-link" @click.prevent="cancelEdit(a)">Cancel</a>
                </template>
                <template v-else>
                  <a href="#" class="ae-link" @click.prevent="startEdit(a)">Edit</a>
                  <a href="#" class="ae-link" @click.prevent="runCell(a)">Run</a>
                  <a v-if="a.data.engine === 'snowflake' || a.data.engine === 'postgres'" href="#" class="ae-link" @click.prevent="downloadBlock(a)">{{ a.downloading ? 'Preparing…' : 'Download' }}</a>
                </template>
              </template>
              <template v-else-if="a.kind === 'draft'">
                <template v-if="a.editing">
                  <a href="#" class="ae-link" @click.prevent="saveDraftEdit(a)">Save edit</a>
                  <a href="#" class="ae-link" @click.prevent="cancelDraftEdit(a)">Cancel</a>
                </template>
                <template v-else>
                  <a href="#" class="ae-link" @click.prevent="startDraftEdit(a)">Edit</a>
                  <a v-if="a.data.kind !== 'schema'" href="#" class="ae-link" @click.prevent="draftAction('preview', a)">Preview</a>
                  <a v-if="a.data.status !== 'saved' && canSaveDraft(a)" href="#" class="ae-link" @click.prevent="draftAction('save', a)">Save</a>
                  <a href="#" class="ae-link" @click.prevent="draftAction('discard', a)">Discard</a>
                </template>
              </template>
            </span>
            </div>
            <div v-if="isOpen(a)" class="ae-fold-body">

            <!-- cell: code, then the selected run's result, then the run history -->
            <div v-if="a.kind === 'cell'" class="ae-cell" :class="{ 'is-editing': a.editing, 'is-flash': a.flash }">
              <div class="ae-cell-box ae-cell-box-code">
                <div class="ae-cell-strip">
                  <span>{{ a.data.engine === 'r' ? 'R script' : 'SQL' }} · v{{ a.data.version || 1 }}</span>
                  <span v-if="a.editing" class="is-muted">editing</span>
                  <span v-else-if="a.data.by === 'user'" class="is-stale">edited by you</span>
                  <span v-else class="is-muted">written by the agent</span>
                </div>
                <div class="ae-cell-code">
                  <textarea
                    v-if="a.editing"
                    v-model="a.edit.code"
                    class="ae-cell-editor"
                    spellcheck="false"
                    :rows="editorRows(a.edit.code)"
                    @keydown.meta.enter.prevent="saveEdit(a)"
                    @keydown.ctrl.enter.prevent="saveEdit(a)"
                    @keydown.esc.prevent="cancelEdit(a)"
                  />
                  <pre v-else class="ae-cell-pre">{{ a.data.code }}</pre>
                </div>
                <div v-if="a.data.frame" class="ae-cell-note">
                  Saved as frame <span>{{ a.data.frame.name }}</span> · {{ formatCount(a.data.frame.rows) }} rows<template v-if="a.data.frame.version !== (a.data.version || 1)"> · from v{{ a.data.frame.version }}</template>
                  · <a href="#" class="ae-link" @click.prevent="fetchFrameFile(a, a.data.frame.id, a.data.frame.name)">CSV</a>
                </div>
              </div>
              <div class="ae-cell-box ae-cell-box-result">
                <div class="ae-cell-strip ae-cell-strip-result">
                  <span>Result<template v-if="selectedRun(a)"> · <span :class="runStatus(selectedRun(a)).tone">{{ runStatus(selectedRun(a)).text }}</span></template></span>
                  <span v-if="selectedRun(a) && selectedRun(a).ran_at" :class="runStale(a, selectedRun(a)) ? 'is-stale' : 'is-muted'">{{ runStale(a, selectedRun(a)) ? 'edited since this run' : 'ran' }} · {{ formatWhen(selectedRun(a).ran_at) }}</span>
                  <span v-else-if="!selectedRun(a)" class="is-muted">not run yet</span>
                </div>
                <div v-if="a.runs.length > 1" class="ae-runs">
                  <button
                    v-for="(r, ri) in a.runs"
                    :key="r.id"
                    type="button"
                    class="ae-run-chip"
                    :class="[runStatus(r).tone, { 'is-on': selectedRunIndex(a) === ri }]"
                    :title="runTitle(r)"
                    @click="a.runIndex = ri"
                  >{{ runShort(r) }}</button>
                </div>
                <template v-if="selectedRun(a)">
                  <div v-if="selectedRun(a).engine === 'r'" class="ae-cell-result ae-cell-result-r">
                    <AgentRunOutput :status="selectedRun(a).status" :error="selectedRun(a).error" :console-text="selectedRun(a).console" :outputs="selectedRun(a).outputs || []" :plot-urls="egressBlocked ? {} : (selectedRun(a).plotUrls || {})" :plots-hidden="egressBlocked" />
                  </div>
                  <div v-else class="ae-cell-result">
                    <div v-if="selectedRun(a).status !== 'completed'" class="ae-cell-error">{{ selectedRun(a).error }}</div>
                    <div v-else-if="!(selectedRun(a).rows || []).length" class="ae-cell-note">No rows.</div>
                    <div v-else class="ae-cell-scroll">
                      <table class="ae-table ae-cell-table">
                        <thead><tr><th v-for="c in selectedRun(a).columns" :key="c">{{ c }}</th></tr></thead>
                        <tbody>
                          <tr v-for="(r, ri) in selectedRun(a).rows" :key="ri"><td v-for="c in selectedRun(a).columns" :key="c" class="ae-mono">{{ r[c] }}</td></tr>
                        </tbody>
                      </table>
                    </div>
                    <div v-if="selectedRun(a).status === 'completed' && selectedRun(a).truncated" class="ae-cell-note">{{ (selectedRun(a).rows || []).length }} of {{ formatCount(selectedRun(a).row_count) }} rows</div>
                  </div>
                </template>
              </div>
              <div v-if="a.error" class="ae-cell-error">{{ a.error }}</div>
            </div>

            <!-- draft: code and spec, then the check or the latest preview -->
            <div v-else-if="a.kind === 'draft'" class="ae-cell ae-draft" :class="{ 'is-editing': a.editing, 'is-flash': a.flash, 'is-unsaved': isUnsaved(a) }">
              <div v-if="codeField(a)" class="ae-cell-box ae-cell-box-code">
                <div class="ae-cell-strip">
                  <span>{{ codeField(a) === 'script' ? 'R script' : 'SQL' }}</span>
                  <span v-if="a.editing" class="is-muted">editing</span>
                  <span v-else-if="a.data.by === 'user'" class="is-stale">edited by you · v{{ a.data.version }}</span>
                  <span v-else class="is-muted">written by the agent · v{{ a.data.version }}</span>
                </div>
                <div v-if="a.data.error" class="ae-cell-error">{{ a.data.error }}</div>
                <div v-if="a.data.saved_at" class="ae-draft-saved">Saved as #{{ a.data.saved_id }} · {{ formatWhen(a.data.saved_at) }}</div>
                <textarea
                  v-if="a.editing"
                  v-model="a.edit.code"
                  class="ae-cell-editor"
                  spellcheck="false"
                  :rows="editorRows(a.edit.code)"
                  @keydown.meta.enter.prevent="saveDraftEdit(a)"
                  @keydown.ctrl.enter.prevent="saveDraftEdit(a)"
                  @keydown.esc.prevent="cancelDraftEdit(a)"
                />
                <pre v-else class="ae-cell-pre">{{ draftCode(a) }}</pre>
              </div>
              <div class="ae-cell-box ae-cell-box-code" :class="{ 'ae-cell-box-json': codeField(a) }">
                <div class="ae-cell-strip ae-cell-strip-toggle" @click="a.collapsed = !a.collapsed">
                  <span><span class="ae-draft-caret">{{ a.collapsed && !a.editing ? '▸' : '▾' }}</span>{{ codeField(a) ? 'Everything else · JSON' : `${a.label} · JSON spec` }}</span>
                  <template v-if="!codeField(a)">
                    <span v-if="a.editing" class="is-muted">editing</span>
                    <span v-else-if="a.data.by === 'user'" class="is-stale">edited by you · v{{ a.data.version }}</span>
                    <span v-else class="is-muted">written by the agent · v{{ a.data.version }}</span>
                  </template>
                </div>
                <template v-if="!codeField(a)">
                  <div v-if="a.data.error" class="ae-cell-error">{{ a.data.error }}</div>
                  <div v-if="a.data.saved_at" class="ae-draft-saved">Saved as #{{ a.data.saved_id }} · {{ formatWhen(a.data.saved_at) }}</div>
                </template>
                <template v-if="a.editing">
                  <textarea
                    v-model="a.edit.json"
                    class="ae-cell-editor ae-draft-editor ae-mono"
                    spellcheck="false"
                    :rows="editorRows(a.edit.json)"
                    @keydown.meta.enter.prevent="saveDraftEdit(a)"
                    @keydown.ctrl.enter.prevent="saveDraftEdit(a)"
                    @keydown.esc.prevent="cancelDraftEdit(a)"
                  />
                  <div v-if="a.editError" class="ae-cell-error">{{ a.editError }}</div>
                </template>
                <pre v-else-if="!a.collapsed" class="ae-cell-pre ae-cell-pre-json">{{ draftJson(a) }}</pre>
              </div>
              <div v-if="a.data.note && !selectedRun(a)" class="ae-cell-box ae-cell-box-result">
                <div class="ae-cell-strip ae-cell-strip-result">
                  <span>Check · <span class="is-ok">valid</span></span>
                  <span v-if="a.data.checked_at" class="is-muted">last check · {{ formatWhen(a.data.checked_at) }}</span>
                </div>
                <div class="ae-cell-note ae-draft-check">{{ a.data.note }}</div>
              </div>
              <div v-if="selectedRun(a)" class="ae-cell-box ae-cell-box-result">
                <div class="ae-cell-strip ae-cell-strip-result">
                  <span>Preview · <span :class="runStatus(selectedRun(a)).tone">{{ runStatus(selectedRun(a)).text }}</span></span>
                  <span v-if="selectedRun(a).ran_at" :class="runStale(a, selectedRun(a)) ? 'is-stale' : 'is-muted'">{{ runStale(a, selectedRun(a)) ? 'edited since preview' : 'last preview' }} · {{ formatWhen(selectedRun(a).ran_at) }}</span>
                </div>
                <div v-if="a.runs.length > 1" class="ae-runs">
                  <button v-for="(r, ri) in a.runs" :key="r.id" type="button" class="ae-run-chip" :class="[runStatus(r).tone, { 'is-on': selectedRunIndex(a) === ri }]" :title="runTitle(r)" @click="a.runIndex = ri">{{ runShort(r) }}</button>
                </div>
                <details v-if="previewDiffers(a, selectedRun(a))" class="ae-draft-compiled">
                  <summary>Compiled SQL</summary>
                  <pre class="ae-cell-pre">{{ selectedRun(a).code }}</pre>
                </details>
                <div class="ae-cell-result">
                  <div v-if="selectedRun(a).status !== 'completed'" class="ae-cell-error">{{ selectedRun(a).error }}</div>
                  <div v-else-if="!(selectedRun(a).rows || []).length" class="ae-cell-note">No rows.</div>
                  <div v-else class="ae-cell-scroll">
                    <table class="ae-table ae-cell-table">
                      <thead><tr><th v-for="c in selectedRun(a).columns" :key="c">{{ c }}</th></tr></thead>
                      <tbody>
                        <tr v-for="(r, ri) in selectedRun(a).rows" :key="ri"><td v-for="c in selectedRun(a).columns" :key="c" class="ae-mono">{{ r[c] }}</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <div v-if="selectedRun(a).status === 'completed' && selectedRun(a).truncated" class="ae-cell-note">{{ (selectedRun(a).rows || []).length }} of {{ formatCount(selectedRun(a).row_count) }} rows</div>
                </div>
              </div>
            </div>

            <!-- everything else -->
            <div v-else class="ae-artifact" :class="{ 'is-flash': a.flash, 'is-unsaved': isUnsaved(a) }">
              <div class="ae-artifact-head">
                <div class="ae-artifact-label">
                  <font-awesome-icon :icon="kindIcon(rowIcon(a))" class="ae-kind-icon" :style="{ color: kindColor(rowIcon(a)) }" />
                  <span class="ae-dot" :style="{ background: rowDot(a) }" />
                  {{ a.label }}<span v-if="rowName(a)" class="ae-artifact-name"> · {{ rowName(a) }}</span>
                </div>
                <div v-if="isVersioned(a)" class="ae-version-bar">
                  <span v-if="versionInfo(a).versions.length > 1" class="ae-version-nav">
                    <button class="ae-version-btn" :disabled="versionInfo(a).index <= 0" title="Earlier version" @click="showVersion(entry, a.key, versionInfo(a).index - 1)">‹</button>
                    <span class="ae-version-n">v{{ versionInfo(a).version.n }} of {{ versionInfo(a).versions.length }}</span>
                    <button class="ae-version-btn" :disabled="versionInfo(a).index >= versionInfo(a).versions.length - 1" title="Later version" @click="showVersion(entry, a.key, versionInfo(a).index + 1)">›</button>
                  </span>
                  <span v-if="versionInfo(a).version.saved" class="ae-save-chip is-saved">Saved · {{ versionInfo(a).version.saved.slug }}</span>
                  <span v-else class="ae-save-chip is-unsaved">Not saved</span>
                  <a v-if="!versionInfo(a).version.saved && canSavePage" href="#" class="ae-link" @click.prevent="savePage(a)">Save</a>
                  <router-link v-else-if="versionInfo(a).version.saved && versionInfo(a).version.saved.page_id" class="ae-link" :to="`/agent/page/${versionInfo(a).version.saved.page_id}`" target="_blank">Open</router-link>
                  <a v-if="versionInfo(a).versions.some((v) => v.saved)" href="#" class="ae-link ae-link-danger" title="Delete the saved page from the platform" @click.prevent="deleteSavedPage(entry, a)">Delete page</a>
                </div>
                <div v-else class="ae-artifact-meta">{{ statusOf(a) }}</div>
              </div>

              <div v-if="a.kind === 'visual'" class="ae-box">
                <div v-if="a.data.rows_deferred && !a.data.rows" class="ae-muted">Loading chart{{ a.data.row_count ? ` (${a.data.row_count.toLocaleString()} rows)` : '' }}…</div>
                <div v-else-if="!a.viz" style="color:#c23a28">Could not render this visual.</div>
                <D3Insight
                  v-else-if="a.viz.component === 'd3'"
                  :code="a.viz.props.code"
                  :rows="a.viz.props.rows"
                  :inputs="a.viz.props.inputs"
                  :min-height="a.viz.props.minHeight"
                />
                <Chart_V6 v-else-if="a.viz.component === 'chart_v6'" :spec="a.viz.props.spec" />
                <div v-else-if="a.viz.component === 'chart_v5'" style="height:300px">
                  <Chart_V5 :chart_data="a.viz.props.chart_data" :chart_config="a.viz.props.chart_config" override-height="h-full" />
                </div>
                <div v-else-if="a.viz.component === 'table'" class="ae-table-scroll">
                  <table class="ae-table">
                    <thead><tr><th v-for="c in a.viz.props.columnDefs" :key="c.field">{{ c.headerName || c.field }}</th></tr></thead>
                    <tbody>
                      <tr v-for="(r, ri) in a.viz.props.rowData" :key="ri">
                        <td v-for="c in a.viz.props.columnDefs" :key="c.field">{{ r[c.field] }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <Pie_Chart v-else-if="a.viz.component === 'pie'" style="height:300px" override-height="h-full" :chart_data="a.viz.props.chart_data" :chart_config="a.viz.props.chart_config" />
                <Sankey_Diagram v-else-if="a.viz.component === 'sankey'" :chart-data="a.viz.props.chartData" :config="a.viz.props.config" />
                <HexMap v-else-if="a.viz.component === 'heatmap'" :chart-data="a.viz.props.chartData" :chart-config="a.viz.props.chartConfig" />
              </div>

              <AgentHtmlCard
                v-else-if="a.kind === 'page'"
                :html="versionInfo(a).version.html"
                :sources="versionInfo(a).version.sources || []"
                :session-id="entry.sessionId || ''"
              />

              <PageCanvas
                v-else-if="a.kind === 'dashboard'"
                :key="`${a.key}:${versionInfo(a).version.n}`"
                :spec="versionInfo(a).version.spec"
                :widgets="versionInfo(a).version.widgets || a.data.widgets || {}"
                :session-id="entry.sessionId || ''"
              />

              <div v-else-if="a.kind === 'drawing'" class="ae-box">
                <iframe :srcdoc="drawingDoc(a.data)" sandbox="" class="ae-iframe" :style="{ aspectRatio: drawingAspect(a) }" :title="a.data.title" />
                <div class="ae-drawing-actions">
                  <a href="#" class="ae-link" @click.prevent="downloadDrawing(a, 'svg')">Download SVG</a>
                  <a href="#" class="ae-link" @click.prevent="downloadDrawing(a, 'png')">Download PNG</a>
                </div>
                <div v-if="a.error" class="ae-cell-error">{{ a.error }}</div>
              </div>

              <div v-else-if="a.kind === 'plan'" class="ae-table-wrap">
                <table class="ae-table">
                  <thead><tr><th>Source</th><th>Role</th><th>Status</th><th>Description</th></tr></thead>
                  <tbody>
                    <tr v-for="src in a.data.sources" :key="src.name">
                      <td class="ae-mono">{{ src.name }}</td>
                      <td class="ae-muted">{{ src.role || '' }}</td>
                      <td><span class="ae-dot" :style="{ background: statusDot(src.status || 'planned') }" /> {{ src.status || 'planned' }}</td>
                      <td class="ae-muted">{{ src.description || '' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <template v-else-if="a.kind === 'proposal'">
                <div class="ae-facts">
                  <div class="ae-fact"><div class="ae-fact-k">Target</div><div class="ae-fact-v ae-mono">{{ a.id }}</div></div>
                  <div class="ae-fact"><div class="ae-fact-k">Key</div><div class="ae-fact-v">{{ a.data.append_only ? 'Append-only' : ((a.data.primary_key_columns || []).join(', ') || 'No key') }}</div></div>
                  <div class="ae-fact"><div class="ae-fact-k">Columns</div><div class="ae-fact-v">{{ (a.data.columns || []).length }}</div></div>
                </div>
                <div v-if="a.data.append_only && a.data.append_only_reason" class="ae-small ae-muted">{{ a.data.append_only_reason }}</div>
                <div v-if="a.data.error" class="ae-small" style="color:#c23a28">{{ a.data.error }}</div>
                <div class="ae-table-wrap ae-table-scroll">
                  <table class="ae-table">
                    <thead><tr><th>Column</th><th>Type</th></tr></thead>
                    <tbody>
                      <tr v-for="col in a.data.columns" :key="col.name || col">
                        <td class="ae-mono">{{ col.name || col }}</td>
                        <td class="ae-muted">{{ col.snowflake_type || 'VARCHAR' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>

              <template v-else-if="a.kind === 'file'">
                <div class="ae-facts">
                  <div class="ae-fact" style="grid-column: span 2"><div class="ae-fact-k">File</div><div class="ae-fact-v ae-mono ae-break">{{ a.data.key }}</div></div>
                  <div class="ae-fact"><div class="ae-fact-k">Shape</div><div class="ae-fact-v">{{ a.data.note }}</div></div>
                </div>
                <div v-if="a.data.rows && a.data.rows.length" class="ae-table-wrap ae-cell-scroll">
                  <table class="ae-table ae-cell-table">
                    <thead><tr><th class="ae-file-rownum">row</th><th v-for="(c, ci) in a.data.rows[0]" :key="ci">{{ ci + 1 }}</th></tr></thead>
                    <tbody>
                      <tr v-for="(r, ri) in a.data.rows" :key="ri">
                        <td class="ae-mono ae-file-rownum">{{ (a.data.offset || 0) + ri }}</td>
                        <td v-for="(c, ci) in r" :key="ci" class="ae-mono">{{ c }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>

              <template v-else-if="a.kind === 'document'">
                <div class="ae-facts">
                  <div class="ae-fact" style="grid-column: span 2"><div class="ae-fact-k">File</div><div class="ae-fact-v ae-mono ae-break">{{ a.data.key }}</div></div>
                  <div class="ae-fact"><div class="ae-fact-k">Shape</div><div class="ae-fact-v">{{ a.data.note }}<template v-if="a.data.total"> · characters {{ (a.data.offset || 0).toLocaleString() }}–{{ (a.data.end || 0).toLocaleString() }} of {{ a.data.total.toLocaleString() }}</template></div></div>
                </div>
                <pre class="ae-doc-text ae-cell-scroll">{{ a.data.text }}</pre>
              </template>

              <template v-else-if="a.kind === 'report'">
                <div class="ae-download">
                  <font-awesome-icon :icon="kindIcon('report')" class="ae-download-icon" />
                  <div class="ae-download-body">
                    <div class="ae-download-title">{{ a.data.title || a.data.name }}</div>
                    <div class="ae-download-meta">PDF<template v-if="a.data.size"> · {{ formatBytes(a.data.size) }}</template></div>
                  </div>
                  <a href="#" class="ae-link ae-btn-tiny" @click.prevent="downloadReport(a)">Download</a>
                </div>
              </template>

              <template v-else-if="a.kind === 'workbook'">
                <div class="ae-download">
                  <font-awesome-icon :icon="kindIcon('workbook')" class="ae-download-icon" />
                  <div class="ae-download-body">
                    <div class="ae-download-title">{{ a.data.title || a.id }}</div>
                    <div class="ae-download-meta">Excel · {{ (a.data.sheets || []).map((s) => s.title).join(' · ') }}</div>
                  </div>
                  <a href="#" class="ae-link ae-btn-tiny" @click.prevent="downloadWorkbook(a)">{{ a.downloading ? 'Preparing…' : 'Download Excel' }}</a>
                </div>
                <div v-if="a.error" class="ae-cell-error">{{ a.error }}</div>
              </template>

              <template v-else-if="a.kind === 'frame'">
                <div class="ae-download">
                  <div class="ae-download-body">
                    <div class="ae-download-title">{{ frameMeta(a) }}</div>
                    <div v-if="frameColumns(a).length" class="ae-download-meta">{{ frameColumns(a).join(' · ') }}</div>
                  </div>
                  <router-link class="ae-link ae-btn-tiny" :to="`/insight/impact-analytics/frames/${a.id}`" target="_blank">Open</router-link>
                  <a href="#" class="ae-link ae-btn-tiny" @click.prevent="downloadFrame(a)">Download CSV</a>
                </div>
              </template>

              <template v-else-if="a.kind === 'widget'">
                <div v-if="a.live && a.live.code && widgetDrawing(a)" class="ae-box">
                  <D3Insight
                    :code="a.live.code"
                    :rows="widgetDrawing(a).viz.props.rows"
                    :inputs="widgetDrawing(a).viz.props.inputs"
                    :min-height="a.live.min_height"
                  />
                </div>
                <div class="ae-facts">
                  <div class="ae-fact"><div class="ae-fact-k">Status</div><div class="ae-fact-v">{{ statusOf(a) }}</div></div>
                  <div v-for="f in factsFor(a)" :key="f.k" class="ae-fact"><div class="ae-fact-k">{{ f.k }}</div><div class="ae-fact-v" :class="{ sensitive: f.k === 'placed on' }">{{ f.v }}</div></div>
                </div>
                <div v-if="a.live && a.live.code && !widgetDrawing(a)" class="ae-cell-note">Not drawn in this session, so there are no rows to preview it with.</div>
              </template>

              <template v-else-if="a.kind === 'template_run'">
                <div class="ae-facts">
                  <div class="ae-fact"><div class="ae-fact-k">Status</div><div class="ae-fact-v">{{ statusOf(a) }}</div></div>
                  <div class="ae-fact"><div class="ae-fact-k">Progress</div><div class="ae-fact-v">{{ a.data.progress || (a.live && a.live.detail && a.live.detail.progress) || '—' }}</div></div>
                  <div class="ae-fact"><div class="ae-fact-k">Run</div><div class="ae-fact-v ae-mono">{{ a.id }}</div></div>
                </div>
                <div v-if="a.data.error || (a.live && a.live.detail && a.live.detail.error)" role="alert" class="ae-cell-note ae-break">{{ a.data.error || a.live.detail.error }}</div>
                <button v-if="['pending', 'running'].includes(statusOf(a)) && !a.cancelling" type="button" class="ae-btn-tiny" @click="cancelTemplateRun(a)">Cancel run</button>
                <span v-else-if="a.cancelling && ['pending', 'running'].includes(statusOf(a))" class="ae-cell-note">Stopping after the current batch…</span>
              </template>

              <template v-else>
                <div class="ae-facts">
                  <div class="ae-fact"><div class="ae-fact-k">Status</div><div class="ae-fact-v">{{ statusOf(a) }}</div></div>
                  <div v-for="f in factsFor(a).slice(0, 5)" :key="f.k" class="ae-fact"><div class="ae-fact-k">{{ f.k }}</div><div class="ae-fact-v">{{ f.v }}</div></div>
                </div>
                <router-link v-if="a.kind === 'saved_page'" class="ae-link ae-btn-tiny" :to="`/agent/page/${a.id}`" target="_blank">Open</router-link>
                <pre v-if="a.live && a.live.script" class="ae-cell-pre ae-file-script">{{ a.live.script }}</pre>
                <div v-for="(r, j) in liveRuns(a)" :key="j" class="ae-file-run">
                  <span class="ae-check" :style="['failed','error'].includes(String(r.status).toLowerCase()) ? 'color:#c23a28' : ''">{{ ['failed','error'].includes(String(r.status).toLowerCase()) ? '✕' : '✓' }}</span>
                  <span class="ae-mono ae-file-run-when">{{ formatWhen(r.when) }}</span>
                  <span class="ae-file-run-text">{{ r.error || r.result }}</span>
                </div>
              </template>
            </div>
            </div>
          </div>

          <template v-if="lane === 'all' || lane === 'uploads'">
            <div v-for="f in s.files" :id="artifactDomId(`upload:${f.key}`)" :key="`upload:${f.key}`" class="ae-fold" :class="{ 'is-open': f.expanded }">
              <div class="ae-fold-row">
              <button type="button" class="ae-fold-head" :title="f.key" @click="f.expanded = !f.expanded">
                <span class="ae-fold-caret">{{ f.expanded ? '▾' : '▸' }}</span>
                <font-awesome-icon :icon="kindIcon('upload')" class="ae-kind-icon" :style="{ color: kindColor('upload') }" />
                <span class="ae-fold-kind">Upload</span>
                <span class="ae-fold-name">{{ f.name }}</span>
                <span class="ae-dot" style="background:#35617f" />
              </button>
              </div>
              <div v-if="f.expanded" class="ae-fold-body">
                <div class="ae-facts">
                  <div class="ae-fact" style="grid-column: span 3"><div class="ae-fact-k">S3 key</div><div class="ae-fact-v ae-mono ae-break">{{ f.key }}</div></div>
                </div>
              </div>
            </div>
          </template>
        </div>
        </div>
      </section>
    </div>

    <AgentUploadModal v-if="showUpload" :session-id="entry.sessionId || ''" @close="showUpload = false" @uploaded="onUploaded" @failed="onUploadFailed" />
  </div>
</template>

<script>
import axios from 'axios';
import { beginSensitiveEgress, sensitiveEgressBlocked } from '@/utils/demoEgress';
import Chart_V5 from '@/components/widgetcomponents/Viz_Elements/Chart_V5.vue';
import Chart_V6 from '@/components/widgetcomponents/Viz_Elements/Chart_V6.vue';
import Sankey_Diagram from '@/components/widgetcomponents/Viz_Elements/Sankey_Diagram.vue';
import HexMap from '@/components/widgetcomponents/Viz_Elements/HexMap.vue';
import Pie_Chart from '@/components/widgetcomponents/Viz_Elements/Pie_Chart.vue';
import { drawingDoc, drawingFile, drawingPng } from '@/utils/agentSandbox';
import { z } from 'zod';
import { BrowserStore } from '@/utils/BrowserStore';
import AgentConversation from './AgentConversation.vue';
import './agent.css';
import AgentHtmlCard from './AgentHtmlCard.vue';
import D3Insight from './D3Insight.vue';
import PageCanvas from './widgets/PageCanvas.vue';
import AgentUploadModal from './AgentUploadModal.vue';
import AgentRunOutput, { plotKey } from './AgentRunOutput.vue';
import {
  faSnowflake, faDatabase, faFlaskVial, faTableColumns, faGaugeHigh, faLayerGroup, faTableList,
  faChartSimple, faPenRuler, faListCheck, faFileLines, faPlay, faFileArrowUp, faFlag, faClipboardList,
  faLightbulb, faGears, faTable, faFilePdf, faMagnifyingGlassChart, faFileArrowUp as faUploadBtn, faWindowMaximize,
  faFileExcel, faTag,
} from '@fortawesome/free-solid-svg-icons';
import { FEATURES } from '@/constants/features';
import { canAccessFeature } from '@/utils/access';
import {
  entryForSession, turnUrl, applyRunOutput, reduceItem, runRecord, applyDraftEdit, applyCellEdit, showVersion, currentVersion,
  removeItem, deleteSavedPage,
} from '@/services/agentSessionController';

const layoutStore = new BrowserStore('agent-layout-v2', z.object({
  conv: z.number().nullable().default(null),
  rail: z.boolean().default(false),
}));
const CONV_MIN = 340;
const WORKSPACE_MIN = 480;
const SPLITTER_W = 20;

const KIND_ICONS = {
  snowflake: faSnowflake, postgres: faDatabase, r: faFlaskVial,
  data_attribute: faTableColumns, metric: faGaugeHigh, dataset: faLayerGroup, study: faFlaskVial, schema: faTableList,
  visual: faChartSimple, drawing: faPenRuler, plan: faListCheck, file: faFileLines, document: faFileLines, proposal: faTableList, run: faPlay,
  inputs: faTable, template_run: faPlay, upload: faFileArrowUp, report: faFilePdf, workbook: faFileExcel, infotag: faTag, flag: faFlag, worklist: faClipboardList, insight: faLightbulb, routine: faGears,
  task_type: faClipboardList, task_action: faPlay, metric_workflow: faListCheck, task_status: faFlag,
  analysis: faMagnifyingGlassChart, page: faWindowMaximize, dashboard: faWindowMaximize, saved_page: faWindowMaximize, frame: faTable, template: faListCheck,
};
const KIND_COLORS = {
  snowflake: '#4f9cc4', postgres: '#35617f', r: '#3b6e93',
  data_attribute: '#35617f', metric: '#35617f', dataset: '#2f8f5f', study: '#7a5aa3', schema: '#9a7b1f',
  visual: '#35617f', drawing: '#7a5aa3', plan: '#56534d', file: '#9a7b1f', document: '#7d6434', proposal: '#9a7b1f', run: '#3b6e93',
  inputs: '#2f8f5f', template_run: '#3b6e93', upload: '#35617f', report: '#a33122', workbook: '#2f7d4f', infotag: '#7a5aa3', flag: '#c23a28', worklist: '#2f8f5f', insight: '#d9a441', routine: '#56534d',
  task_type: '#3b6e93', task_action: '#7a5aa3', metric_workflow: '#35617f', task_status: '#56534d',
  analysis: '#2f8f5f', frame: '#2f8f5f', template: '#56534d',
};

const LANES = {
  queries: { label: 'Queries', match: (a) => a.kind === 'cell' },
  drafts: { label: 'Drafts', match: (a) => a.kind === 'draft' },
  visuals: { label: 'Visuals', match: (a) => a.kind === 'visual' || a.kind === 'drawing' },
  pages: { label: 'Pages', match: (a) => ['page', 'dashboard', 'saved_page'].includes(a.kind) },
  files: { label: 'Files', match: (a) => ['file', 'document', 'report', 'workbook'].includes(a.kind) },
  runs: { label: 'Runs', match: (a) => ['template_run', 'plan', 'proposal'].includes(a.kind) },
  saved: { label: 'Saved', match: (a) => !!a.live || (a.kind === 'draft' && !!a.data.saved_id) },
};
const DRAFT_SAVE_FEATURE = {
  data_attribute: FEATURES.ACTION_AGENT_SAVE_METRICS, metric: FEATURES.ACTION_AGENT_SAVE_METRICS,
  dataset: FEATURES.ACTION_AGENT_SAVE_STUDIES, study: FEATURES.ACTION_AGENT_SAVE_STUDIES,
  schema: FEATURES.ACTION_AGENT_SAVE_INGESTION,
};

export default {
  name: 'AgentExplore',
  components: {
    Chart_V5, Chart_V6, Sankey_Diagram, HexMap, Pie_Chart,
    AgentConversation, AgentUploadModal, AgentRunOutput, AgentHtmlCard, D3Insight, PageCanvas,
  },
  props: {
    autoStart: { type: Boolean, default: false },
    sessionId: { type: String, default: null },
    metricId: { type: [Number, String], default: null },
    studyId: { type: [Number, String], default: null },
    s3Key: { type: String, default: '' },
    schemaId: { type: [Number, String], default: null },
    hats: { type: String, default: '' },
    profile: { type: String, default: '' },
  },
  data() {
    const entry = entryForSession(
      this.sessionId,
      `${this.$store.state.tenant.current_tenant_slug || ''}:${this.$store.state.auth.current_user?.id || ''}:${this.metricId || ''}:${this.studyId || ''}:${this.s3Key || ''}`,
    );
    return {
      entry,
      s: entry.state,
      showUpload: false,
      runPollTimers: {},
      uploadIcon: faUploadBtn,
      convWidth: layoutStore.load().conv,
      resizing: false,
      lane: 'all',
    };
  },
  computed: {
    egressBlocked() {
      return sensitiveEgressBlocked();
    },
    context() {
      return { metricId: this.metricId, studyId: this.studyId, s3Key: this.s3Key, schemaId: this.schemaId, hats: this.hats, profile: this.profile };
    },
    storageKey() {
      return `agent-session:${this.$store.state.tenant.current_tenant_slug || ''}:${this.$store.state.auth.current_user?.id || ''}:${this.metricId || ''}:${this.studyId || ''}:${this.s3Key || ''}${this.profile ? `:${this.profile}` : ''}`;
    },
    sessionActive() {
      return ['connecting', 'launching', 'ready', 'thinking', 'rate_limited'].includes(this.s.connState);
    },
    canUpload() {
      return canAccessFeature(FEATURES.ACTION_AGENT_UPLOAD);
    },
    canSavePage() {
      return canAccessFeature(FEATURES.ACTION_AGENT_SAVE_PAGES);
    },
    items() {
      return [...this.s.items].sort((x, y) => (x.order || 0) - (y.order || 0));
    },
    savedItems() {
      return this.items.filter(LANES.saved.match);
    },
    lanes() {
      const out = [{ id: 'all', label: 'All', count: this.items.length + this.s.files.length }];
      Object.entries(LANES).forEach(([id, lane]) => {
        const count = this.items.filter(lane.match).length;
        if (count > 0) out.push({ id, label: lane.label, count });
      });
      if (this.s.files.length) out.push({ id: 'uploads', label: 'Uploads', count: this.s.files.length });
      return out;
    },
    visibleItems() {
      if (this.lane === 'all') return this.items;
      if (this.lane === 'uploads') return [];
      return LANES[this.lane] ? this.items.filter(LANES[this.lane].match) : [];
    },
    sessionPill() {
      const drafts = this.items.filter((a) => a.kind === 'draft' && a.data.status !== 'saved').length;
      const saved = this.savedItems;
      if (drafts && !saved.length) return { text: `${drafts} ${drafts === 1 ? 'draft' : 'drafts'} · nothing saved`, bg: '#f0eee8', fg: '#56534d', dot: '#d9a441' };
      const inProd = saved.some((a) => (a.live && a.live.runs && a.live.runs.length) || (a.live && a.live.status === 'running in production'));
      if (inProd) return { text: 'In production', bg: '#e7f2ea', fg: '#1f6a45', dot: '#2f8f5f' };
      if (saved.length) return { text: 'Saved', bg: '#f0eee8', fg: '#56534d', dot: '#2f8f5f' };
      return { text: 'Nothing saved', bg: '#f0eee8', fg: '#56534d', dot: '#b0aca3' };
    },
  },
  watch: {
    // R plot PNGs are drawn server-side from arbitrary scripts, so their text
    // cannot be masked. Drop them when Demo Mode starts, fetch them again when
    // it ends.
    egressBlocked(blocked) {
      if (blocked) this.dropRunPlots();
      else this.restoreRunPlots();
    },
  },
  mounted() {
    this.entry.view = this;
    // The session entry (and its plot blob URLs) outlives this view, so Demo
    // Mode may have started or ended while it was unmounted.
    if (this.egressBlocked) this.dropRunPlots();
    else this.restoreRunPlots();
    this.s.items.forEach((a) => {
      a.runs.filter((r) => r.engine === 'r' && ['pending', 'running'].includes(r.status)).forEach((r) => this.startRunPolling(r.id));
      if (a.kind === 'template_run' && ['pending', 'running'].includes(this.statusOf(a))) this.startTemplateRunPolling(a.id);
    });
  },
  beforeUnmount() {
    if (this.entry.view === this) this.entry.view = null;
    Object.keys(this.runPollTimers).forEach((id) => this.stopRunPolling(id));
  },
  methods: {
    showVersion,
    removeItem,
    deleteSavedPage,
    drawingDoc,
    isVersioned(a) {
      return a.kind === 'page' || a.kind === 'dashboard';
    },
    versionInfo(a) {
      return currentVersion(a);
    },
    isUnsaved(a) {
      if (!a) return false;
      if (a.kind === 'draft') return a.data.status !== 'saved';
      if (this.isVersioned(a)) return !this.versionInfo(a).version.saved;
      return false;
    },
    savePage(a) {
      const { version } = this.versionInfo(a);
      if (!version || !this.$refs.conversation) return;
      this.$refs.conversation.sendPageSave(a, version);
    },
    isOpen(a) {
      return !!a.expanded;
    },
    toggleFold(a) {
      a.expanded = !a.expanded;
    },
    rowIcon(a) {
      if (a.kind === 'cell') return a.data.engine || 'snowflake';
      if (a.kind === 'draft') return a.data.kind;
      return a.kind;
    },
    rowName(a) {
      if (a.kind === 'cell') return a.data.label || a.id;
      if (a.kind === 'draft') return a.data.name || a.id;
      if (a.live && a.live.name) return a.live.name;
      return a.data.title || a.data.name || a.id;
    },
    rowHint(a) {
      if (a.kind === 'report') return `Download ${a.data.name}`;
      if (a.kind === 'workbook') return `Download ${a.data.title || a.id} as Excel`;
      return `${a.label} · ${this.statusOf(a)}`;
    },
    isMono(a) {
      return ['file', 'document', 'proposal', 'schema'].includes(a.kind) || (a.kind === 'cell' && !a.data.label);
    },
    statusOf(a) {
      if (a.live && a.live.status) return a.live.status;
      if (a.data.status) return a.data.status;
      if (a.kind === 'cell') return this.selectedRun(a) ? this.runStatus(this.selectedRun(a)).text : 'not run';
      if (a.kind === 'draft') return a.data.status || 'draft';
      if (a.kind === 'template_run') return 'pending';
      return this.isVersioned(a) ? (this.isUnsaved(a) ? 'shown' : 'saved') : 'shown';
    },
    rowDot(a) {
      if (a.kind === 'draft') return this.draftDot(a);
      if (a.kind === 'cell') {
        const run = this.selectedRun(a);
        return run ? (this.runStatus(run).tone === 'is-error' ? '#c23a28' : '#2f8f5f') : '#b0aca3';
      }
      return this.statusDot(this.statusOf(a));
    },
    selectedRunIndex(a) {
      if (!a.runs.length) return -1;
      return typeof a.runIndex === 'number' && a.runIndex >= 0 && a.runIndex < a.runs.length ? a.runIndex : a.runs.length - 1;
    },
    selectedRun(a) {
      const i = this.selectedRunIndex(a);
      return i >= 0 ? a.runs[i] : null;
    },
    runStale(a, run) {
      return !!(run && run.version && a.data.version && run.version !== a.data.version);
    },
    runStatus(r) {
      if (!r) return { text: 'not run', tone: 'is-muted' };
      if (r.engine === 'r') {
        if (['pending', 'running'].includes(r.status)) return { text: r.status === 'pending' ? 'queued' : 'running…', tone: 'is-busy' };
        if (r.status === 'failed') return { text: 'failed', tone: 'is-error' };
        return { text: `run ${r.id} · ${(r.outputs || []).length} outputs`, tone: 'is-ok' };
      }
      if (r.status !== 'completed') return { text: 'error', tone: 'is-error' };
      const secs = typeof r.elapsed_ms === 'number' ? ` · ${(r.elapsed_ms / 1000).toFixed(1)}s` : '';
      return { text: `${this.formatCount(r.row_count)} rows${secs}`, tone: 'is-ok' };
    },
    runShort(r) {
      const mark = r.status === 'failed' ? '✕' : (['pending', 'running'].includes(r.status) ? '…' : '✓');
      return `${r.engine === 'r' ? `run ${r.id}` : `#${r.n || r.id.split('#').pop()}`} ${mark}`;
    },
    runTitle(r) {
      return `${this.runStatus(r).text}${r.ran_at ? ` · ${this.formatWhen(r.ran_at)}` : ''}`;
    },
    liveRuns(a) {
      return ((a.live && a.live.runs) || []).slice(0, 5);
    },
    widgetDrawing(a) {
      const slug = a.live && a.live.slug;
      if (!slug) return null;
      const title = String(a.live.name || '').trim().toLowerCase();
      const drawn = this.items.filter((it) => it.kind === 'visual' && it.viz && it.viz.component === 'd3'
        && (it.id === `widget_${slug}` || String(it.data.title || '').trim().toLowerCase() === title));
      return drawn.length ? drawn[drawn.length - 1] : null;
    },
    factsFor(a) {
      const facts = [];
      Object.entries((a.live && a.live.detail) || {}).forEach(([k, v]) => {
        if (v === '' || v === null || v === undefined) return;
        facts.push({ k: String(k).replace(/_/g, ' '), v: Array.isArray(v) ? v.join(', ') : (typeof v === 'object' ? JSON.stringify(v) : String(v)) });
      });
      return facts;
    },
    frameRows(a) {
      const d = (a.live && a.live.detail) || {};
      return d.rows ?? a.data.rows ?? null;
    },
    frameColumns(a) {
      return (a.live && a.live.detail && a.live.detail.columns) || a.data.columns || [];
    },
    frameMeta(a) {
      const d = (a.live && a.live.detail) || {};
      const p = d.provenance || {};
      const rows = this.frameRows(a);
      const cols = this.frameColumns(a).length;
      const source = { hermes_run: `Hermes run ${p.run_id}`, sql: 'SQL query', upload: 'Upload' }[p.kind] || '';
      return [rows != null && `${this.formatCount(rows)} rows`, cols && `${cols} columns`, d.bytes && this.formatBytes(d.bytes), source].filter(Boolean).join(' · ');
    },
    startResize(e) {
      if (e.button !== 0) return;
      const conv = this.$refs.conversation && this.$refs.conversation.$el;
      if (!conv) return;
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = conv.getBoundingClientRect().width;
      const max = Math.max(CONV_MIN, this.$refs.grid.getBoundingClientRect().width - SPLITTER_W - WORKSPACE_MIN);
      this.resizing = true;
      const move = (ev) => {
        this.convWidth = Math.round(Math.min(max, Math.max(CONV_MIN, startWidth + ev.clientX - startX)));
      };
      const stop = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', stop);
        window.removeEventListener('pointercancel', stop);
        this.resizing = false;
        layoutStore.save({ conv: this.convWidth });
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', stop);
      window.addEventListener('pointercancel', stop);
    },
    resetSplit() {
      this.convWidth = null;
      layoutStore.save({ conv: null });
    },
    onSession(sessionId) {
      if (!sessionId || sessionId === this.$route.params.sessionId) return;
      this.$router.replace({ name: 'Agent', params: { sessionId }, query: this.$route.query });
    },
    onReset() {
      Object.keys(this.runPollTimers).forEach((id) => this.stopRunPolling(id));
    },
    scrollChat() {
      if (this.$refs.conversation) this.$refs.conversation.scrollChat();
    },
    formatBytes(n) {
      if (!n) return '';
      return n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`;
    },
    async downloadDrawing(a, format) {
      const ticket = beginSensitiveEgress('download');
      if (!ticket) return;
      a.error = '';
      const name = String(a.data.title || a.id || 'drawing').replace(/[^a-z0-9]+/gi, '_').replace(/^_+|_+$/g, '') || 'drawing';
      let blob;
      if (format === 'svg') {
        const file = drawingFile(a.data.svg);
        if (!file) {
          a.error = 'This drawing has no SVG to download.';
          return;
        }
        blob = new Blob([file], { type: 'image/svg+xml' });
      } else {
        try {
          blob = await drawingPng(a.data.svg);
        } catch (err) {
          a.error = `${err.message}. Download the SVG instead.`;
          return;
        }
      }
      if (!ticket.confirm()) return;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${name}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    },
    downloadReport(a) {
      this.$refs.conversation.downloadFile({ name: a.data.name, s3Key: a.data.key });
    },
    downloadFrame(a) {
      return this.fetchFrameFile(a, a.id, this.rowName(a));
    },
    async fetchFrameFile(a, frameId, name, ticket = beginSensitiveEgress('download')) {
      if (!ticket) return;
      try {
        const { data } = await axios.get(`/api/impact_analytics/frames/${frameId}/download/`, { responseType: 'blob' });
        if (!ticket.confirm()) return;
        const url = URL.createObjectURL(data);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${String(name || 'frame').replace(/[^a-z0-9]+/gi, '_')}_${frameId}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      } catch (err) {
        a.error = err?.response?.status === 403 ? 'Your role cannot download patient-level frames.' : 'The frame could not be downloaded.';
      }
    },
    async downloadWorkbook(a) {
      if (a.downloading) return;
      const ticket = beginSensitiveEgress('download');
      if (!ticket) return;
      a.downloading = true;
      a.error = '';
      try {
        const { data } = await axios.post(`/api/impact_analytics/agent-sessions/${this.entry.sessionId}/workbook/`, {
          name: a.data.title || a.id, sheets: a.data.sheets || [],
        });
        let job = { status: 'pending' };
        for (let i = 0; i < 300 && ticket.active && ['pending', 'in_progress'].includes(job.status); i += 1) {
          await new Promise((resolve) => { setTimeout(resolve, 2000); });
          job = (await axios.get(`/api/jobs/${data.job_id}/status/`)).data;
        }
        if (!ticket.confirm()) return;
        if (job.status !== 'completed') {
          a.error = job.error_message || (job.status === 'failed' ? 'The workbook could not be built.' : 'The workbook is taking too long; try again shortly.');
          return;
        }
        const cut = (job.result_data.sheets || []).filter((s) => s.truncated).map((s) => s.title);
        if (cut.length) a.error = `Excel holds at most 1,048,575 rows a sheet; ${cut.join(', ')} was cut there.`;
        await this.$refs.conversation.downloadFile({ name: job.result_data.name, s3Key: job.result_data.key });
      } catch (err) {
        a.error = err?.response?.status === 403 ? 'Your role cannot download patient-level data.' : (err?.response?.data?.error || 'The download failed.');
      } finally {
        a.downloading = false;
      }
    },
    async downloadBlock(a) {
      if (a.downloading) return;
      const ticket = beginSensitiveEgress('download');
      if (!ticket) return;
      a.downloading = true;
      a.error = '';
      try {
        const { data } = await axios.post(`/api/impact_analytics/agent-sessions/${this.entry.sessionId}/blocks/${encodeURIComponent(a.id)}/frame/`);
        let job = { status: 'pending' };
        for (let i = 0; i < 300 && ticket.active && ['pending', 'in_progress'].includes(job.status); i += 1) {
          await new Promise((resolve) => { setTimeout(resolve, 2000); });
          job = (await axios.get(`/api/jobs/${data.job_id}/status/`)).data;
        }
        if (!ticket.confirm()) return;
        if (job.status !== 'completed') {
          a.error = job.error_message || (job.status === 'failed' ? 'The query could not be saved.' : 'The download is taking too long; try again shortly.');
          return;
        }
        await this.fetchFrameFile(a, job.result_data.frame_id, job.result_data.name, ticket);
      } catch (err) {
        a.error = err?.response?.status === 403 ? 'Your role cannot download patient-level data.' : (err?.response?.data?.error || 'The download failed.');
      } finally {
        a.downloading = false;
      }
    },
    onUploaded(file) {
      this.$refs.conversation.attachUpload(file);
    },
    onUploadFailed(file) {
      this.$refs.conversation.uploadFailed(file);
    },
    startRunPolling(runId) {
      if (!runId || this.runPollTimers[runId]) return;
      const poll = async () => {
        try {
          const { data } = await axios.get(`/api/impact_analytics/study-runs/${runId}/`);
          applyRunOutput(this.entry, {
            run_id: data.id,
            status: data.status,
            error: data.error_message || '',
            console: data.console_output || '',
            results: data.results || {},
          });
        } catch {
          this.stopRunPolling(runId);
        }
      };
      this.runPollTimers[runId] = setInterval(poll, 2500);
      poll();
    },
    startTemplateRunPolling(runId) {
      const key = `template:${runId}`;
      if (!runId || this.runPollTimers[key]) return;
      const poll = async () => {
        try {
          const { data } = await axios.get(`/api/hermes/runs/${runId}/`);
          const p = data.progress || {};
          const progress = p.units ? `${(p.done || 0) + (p.failed || 0)} of ${p.units} units${p.failed ? `, ${p.failed} failed` : ''}` : (p.turns ? `${p.turns} turns` : '');
          reduceItem(this.entry, { op: 'put', key: `template_run:${runId}`, if_exists: true, data: { status: data.status, progress, error: data.error || '' } });
          if (!['pending', 'running'].includes(data.status)) this.stopRunPolling(key);
        } catch {
          this.stopRunPolling(key);
        }
      };
      this.runPollTimers[key] = setInterval(poll, 5000);
      poll();
    },
    async cancelTemplateRun(a) {
      a.cancelling = true;
      try {
        await axios.post(`/api/hermes/runs/${a.id}/cancel/`);
      } catch (error) {
        a.cancelling = false;
        a.error = error.response?.data?.error || 'Cancel failed.';
      }
    },
    stopRunPolling(runId) {
      if (this.runPollTimers[runId]) clearInterval(this.runPollTimers[runId]);
      delete this.runPollTimers[runId];
    },
    async fetchRunPlots(runId) {
      const run = runRecord(this.entry, runId);
      if (!run || run.status !== 'completed') return;
      if (sensitiveEgressBlocked()) {
        run.plotsHidden = true;
        return;
      }
      const epoch = this.entry.plotEpoch || 0;
      run.plotsLoading = true;
      const created = [];
      const urls = {};
      const have = run.plotUrls || {};
      for (const output of (run.outputs || []).filter((o) => o.kind === 'plot')) {
        const file = plotKey(output);
        const name = file.replace(/^plots\//, '').replace(/\.png$/, '');
        if (!name || have[file]) {
          urls[file] = have[file] || '';
          continue;
        }
        try {
          const response = await axios.get(
            `/api/impact_analytics/study-runs/${runId}/artifact/`,
            { params: { name }, responseType: 'blob' },
          );
          // Demo Mode started (or ended and restarted) while this was in flight.
          if (sensitiveEgressBlocked() || epoch !== (this.entry.plotEpoch || 0)) {
            // dropRunPlots marked this run; a newer fetch (restoreRunPlots) owns it.
            created.forEach((url) => URL.revokeObjectURL(url));
            return;
          }
          urls[file] = URL.createObjectURL(response.data);
          created.push(urls[file]);
        } catch {
          urls[file] = '';
        }
      }
      // A later artifact may have failed after Demo Mode started.
      if (sensitiveEgressBlocked() || epoch !== (this.entry.plotEpoch || 0)) {
        created.forEach((url) => URL.revokeObjectURL(url));
        return;
      }
      run.plotUrls = urls;
      run.plotsLoading = false;
    },
    // Revokes every plot blob URL in this session and marks the run so
    // restoreRunPlots fetches it again once Demo Mode ends.
    dropRunPlots() {
      this.entry.plotEpoch = (this.entry.plotEpoch || 0) + 1;
      this.s.items.forEach((a) => (a.runs || []).forEach((run) => {
        const urls = Object.values(run.plotUrls || {}).filter(Boolean);
        if (!urls.length && !run.plotsLoading) return;
        urls.forEach((url) => URL.revokeObjectURL(url));
        run.plotUrls = {};
        run.plotsLoading = false;
        run.plotsHidden = true;
      }));
    },
    restoreRunPlots() {
      this.s.items.forEach((a) => (a.runs || []).forEach((run) => {
        if (!run.plotsHidden) return;
        run.plotsHidden = false;
        this.fetchRunPlots(run.id);
      }));
    },
    kindIcon(kind) {
      return KIND_ICONS[kind] || faFileLines;
    },
    kindColor(kind) {
      return KIND_COLORS[kind] || '#56534d';
    },
    editorRows(text) {
      return Math.min(Math.max(String(text || '').split('\n').length + 1, 3), 30);
    },
    formatCount(n) {
      return typeof n === 'number' ? n.toLocaleString() : String(n ?? '');
    },
    startEdit(a) {
      a.edit = { code: a.data.code };
      a.editing = true;
      a.error = '';
    },
    cancelEdit(a) {
      a.editing = false;
      a.edit = {};
    },
    async saveEdit(a) {
      const code = String(a.edit.code || '');
      if (!code.trim() || a.saving) return;
      if (code === a.data.code) {
        this.cancelEdit(a);
        return;
      }
      a.saving = true;
      a.error = '';
      try {
        const { data } = await axios.post(turnUrl(this.entry), {
          type: 'block_edit', block_id: a.id, engine: a.data.engine, code,
        });
        if (data.seq) this.entry.ownSeqs.add(data.seq);
        applyCellEdit(this.entry, a.id, a.data.engine, code);
        a.editing = false;
        a.edit = {};
      } catch (err) {
        a.error = (err && err.response && err.response.data && err.response.data.error) || 'Could not save the edit.';
      } finally {
        a.saving = false;
      }
    },
    runCell(a) {
      if (a.editing) return;
      this.$refs.conversation.sendAction('run', a.id);
    },
    canSaveDraft(a) {
      const feature = DRAFT_SAVE_FEATURE[a.data.kind];
      return !feature || canAccessFeature(feature);
    },
    draftAction(verb, a) {
      if (a.editing) return;
      this.$refs.conversation.sendAction(verb, a.id);
    },
    draftDot(a) {
      if (a.data.error) return '#c23a28';
      if (a.data.status === 'saved') return '#2f8f5f';
      if (a.data.status === 'changed') return '#d9a441';
      return '#b0aca3';
    },
    draftChip(a) {
      if (a.data.status === 'saved') return `Saved #${a.data.saved_id}`;
      if (a.data.status === 'changed') return `Changed since save #${a.data.saved_id}`;
      return 'Draft';
    },
    codeField(a) {
      if (a.data.kind === 'data_attribute') return 'sql_query';
      if (a.data.kind === 'study') return 'script';
      return '';
    },
    draftJson(a) {
      const rest = { ...(a.data.spec || {}) };
      const field = this.codeField(a);
      if (field) delete rest[field];
      return JSON.stringify(rest, null, 2);
    },
    draftCode(a) {
      const spec = a.data.spec || {};
      if (a.data.kind === 'data_attribute') return spec.sql_query || '';
      if (a.data.kind === 'study') return spec.script || '';
      return '';
    },
    startDraftEdit(a) {
      a.collapsed = false;
      a.edit = { json: this.draftJson(a), code: this.codeField(a) ? ((a.data.spec || {})[this.codeField(a)] || '') : '' };
      a.editing = true;
      a.editError = '';
    },
    cancelDraftEdit(a) {
      a.editing = false;
      a.edit = {};
    },
    async saveDraftEdit(a) {
      if (a.saving) return;
      let spec;
      try {
        spec = JSON.parse(a.edit.json || '');
      } catch (err) {
        a.editError = `Not valid JSON: ${err.message}`;
        return;
      }
      if (!spec || typeof spec !== 'object' || Array.isArray(spec)) {
        a.editError = 'The spec must be a JSON object.';
        return;
      }
      const field = this.codeField(a);
      if (field) {
        if (!String(a.edit.code || '').trim()) { a.editError = `${field === 'script' ? 'The script' : 'The SQL'} cannot be empty.`; return; }
        spec[field] = a.edit.code;
      }
      if (JSON.stringify(spec) === JSON.stringify(a.data.spec)) { this.cancelDraftEdit(a); return; }
      a.saving = true;
      a.editError = '';
      try {
        const { data } = await axios.post(turnUrl(this.entry), { type: 'draft_edit', draft_id: a.id, spec });
        if (data.seq) this.entry.ownSeqs.add(data.seq);
        applyDraftEdit(this.entry, a.id, spec);
        a.editing = false;
        a.edit = {};
      } catch (err) {
        a.editError = (err && err.response && err.response.data && err.response.data.error) || 'Could not save the edit.';
      } finally {
        a.saving = false;
      }
    },
    previewDiffers(a, run) {
      if (!run || !run.code) return false;
      const inner = String(run.code).replace(/^SELECT \* FROM \(\s*/i, '').replace(/\s*\) _preview LIMIT \d+\s*$/i, '').trim();
      return inner !== String(this.draftCode(a) || '').trim();
    },
    statusDot(status) {
      const st = String(status || '').toLowerCase();
      if (['running in production', 'completed', 'live', 'success', 'succeeded', 'validated', 'verified', 'saved', 'created'].includes(st)) return '#2f8f5f';
      if (['failed', 'rejected', 'deleted', 'error'].includes(st)) return '#c23a28';
      if (['running', 'queued', 'pending', 'in_progress', 'building', 'proposed'].includes(st)) return '#d9a441';
      return '#b0aca3';
    },
    scrollArtifacts() {
      this.$nextTick(() => {
        const el = this.$refs.artifactScroll;
        if (el) el.scrollTop = el.scrollHeight;
      });
    },
    artifactDomId(key) {
      return `artifact-${key.replace(/[^a-zA-Z0-9_-]/g, '_')}`;
    },
    scrollToArtifact(key) {
      let target = this.s.items.find((a) => a.key === key);
      if (!target && key && key.startsWith('page:')) target = [...this.s.items].reverse().find((a) => a.kind === 'page');
      if (!target) return;
      if (this.lane !== 'all' && !this.visibleItems.includes(target)) this.lane = 'all';
      target.expanded = true;
      target.collapsed = false;
      target.flash = true;
      setTimeout(() => { target.flash = false; }, 1200);
      this.$nextTick(() => {
        const el = document.getElementById(this.artifactDomId(target.key));
        const sc = this.$refs.artifactScroll;
        if (el && sc) {
          const top = el.getBoundingClientRect().top - sc.getBoundingClientRect().top + sc.scrollTop - 16;
          sc.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
        } else if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    drawingAspect(a) {
      const m = /viewBox\s*=\s*["']\s*[\d.-]+\s+[\d.-]+\s+([\d.]+)\s+([\d.]+)/.exec(a.data.svg || '');
      if (m && Number(m[2]) > 0) return `${m[1]} / ${m[2]}`;
      return '2 / 1';
    },
    formatWhen(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return iso;
      return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
  },
};
</script>
